import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister } from '@lexical/utils'
import { WhatsAppBusinessTemplate } from '@missionlabs/types'
import { getDestinationOSName } from '@missionlabs/utils'
import isElectron from 'is-electron'
import {
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    CLEAR_EDITOR_COMMAND,
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_HIGH,
    createCommand,
    KEY_DOWN_COMMAND,
    KEY_ENTER_COMMAND,
    LexicalCommand,
    PASTE_COMMAND
} from 'lexical'
import { FC, useCallback, useEffect } from 'react'

export const SMS_CHARACTER_LIMIT = 765
export const SMS_MAX_MESSAGE_LENGTH = 160
export const WHATSAPP_CHARACTER_LIMIT = 4096
export const WHATSAPP_MAX_MESSAGE_LENGTH = 4096

// https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/getModifierState#modifier_keys_on_gecko
function getPlatformModifier() {
    const platform = isElectron()
        ? getDestinationOSName(window.platform)
        : window.navigator.platform
    if (/Mac|iPod|iPhone|iPad/i.test(platform)) return 'Meta'
    return 'Control'
}

export const SEND_MESSAGE_COMMAND: LexicalCommand<void> = createCommand('SEND_MESSAGE_COMMAND')

export interface SMSSendMessagePluginProps {
    onSendMessage: (message: { text: string }) => void
    channelID: string
    isWhatsApp: boolean
    template?: WhatsAppBusinessTemplate
    isReadOnly?: boolean
    limitExceeded?: boolean
}

export const SMSSendMessagePlugin: FC<SMSSendMessagePluginProps> = ({
    onSendMessage,
    channelID,
    template,
    isWhatsApp,
    isReadOnly,
    limitExceeded
}) => {
    const [editor] = useLexicalComposerContext()

    const characterLimit = isWhatsApp ? WHATSAPP_CHARACTER_LIMIT : SMS_CHARACTER_LIMIT

    useEffect(() => {
        if (template) {
            const body = template.components.find(component => component.type === 'BODY')
            const text = body?.text ?? ''
            editor.update(() => {
                const root = $getRoot()
                root.clear()
                const p = $createParagraphNode()
                p.append($createTextNode(text))
                root.append(p)
            })
        } else {
            editor.update(() => {
                const root = $getRoot()
                root.clear()
            })
        }
        editor.setEditable(!isReadOnly && !limitExceeded)
    }, [editor, isReadOnly, limitExceeded, template])

    const handleSendMessage = useCallback(() => {
        //remove draft message form storage
        sessionStorage.removeItem(channelID)
        const text = editor.getRootElement()?.textContent ?? ''
        const message = {
            text
        }
        onSendMessage({ ...message })
        editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined)
        return true
    }, [channelID, editor, onSendMessage])

    useEffect(() => {
        return mergeRegister(
            editor.registerCommand(
                SEND_MESSAGE_COMMAND,
                handleSendMessage,
                COMMAND_PRIORITY_EDITOR
            ),
            editor.registerCommand(
                KEY_ENTER_COMMAND,
                event => {
                    // user must press platform modifier + enter in order to send a message
                    const modifier = event?.getModifierState(getPlatformModifier())
                    if (!modifier) return false
                    event?.preventDefault()
                    return handleSendMessage()
                },
                COMMAND_PRIORITY_HIGH
            ),
            editor.registerCommand(
                KEY_DOWN_COMMAND,
                event => {
                    const selection = $getSelection()
                    const text = $getRoot().getTextContent()

                    // Prevent keypress if the text exceeds character limit
                    if (
                        text.length >= characterLimit &&
                        event.key.length === 1 &&
                        $isRangeSelection(selection) &&
                        selection.isCollapsed()
                    ) {
                        event.preventDefault()
                        return true
                    }

                    return false
                },
                COMMAND_PRIORITY_HIGH
            ),
            editor.registerCommand(
                PASTE_COMMAND,
                event => {
                    const clipboardEvent = event as ClipboardEvent
                    const pastedText = clipboardEvent.clipboardData?.getData('text/plain') ?? ''

                    editor.update(() => {
                        const currentText = $getRoot().getTextContent()
                        const selection = $getSelection()

                        if (!$isRangeSelection(selection)) return

                        const selectedLength = selection.getTextContent().length
                        const availableSpace =
                            characterLimit - (currentText.length - selectedLength)

                        // If the pasted text exceeds the character limit, trim it
                        if (availableSpace <= 0) {
                            clipboardEvent.preventDefault()
                            return
                        }

                        const trimmedText = pastedText.slice(0, availableSpace)
                        if (trimmedText.length > 0) selection.insertText(trimmedText)

                        clipboardEvent.preventDefault()
                    })

                    return true
                },
                COMMAND_PRIORITY_HIGH
            )
        )
    }, [characterLimit, editor, handleSendMessage])

    return null
}
